import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { useParams } from "react-router";
import { useMemo, useState } from "react";

import { useCarrierBonusByCarrierIdQuery } from "../../hooks/useBbe";
import { formatCurrencyWithRound } from "../../utils";
import { CircularProgressCentered } from "../../components/CircularProgressCentered";
import { ROUTE_NAMES } from "../../constants";

import {
  CARRIER_ASSETS,
  CARRIER_BONUS_DETAIL_BUSINESS_TITLE,
  CARRIER_BONUS_DETAIL_PERSISTENCY_TITLE,
  CarrierName,
} from "./constants";
import { CarrierRulesDrawer } from "./components/carrierRulesDrawer";
import { InOutTable } from "./components/inOutTable";
import { BackNavigationButton } from "./components/BackNavigationButton";
import { formatNumberValue } from "./utils";

export const CarrierBonusDetail: React.FC = () => {
  const { id: brokerIdParam, carrierId } = useParams<{
    id: string;
    carrierId: string;
  }>();
  const brokerId = brokerIdParam ?? "";
  const { data, isLoading, isRefetching } = useCarrierBonusByCarrierIdQuery(
    brokerId,
    carrierId ?? "",
  );

  const projectedBonus = formatCurrencyWithRound(
    data?.total_projected_bonus ?? 0,
  );

  const [open, setOpen] = useState<boolean>(false);

  const imageSrc = useMemo(() => {
    let url = "";

    const carrierNameLower = (data?.carrier_name ?? "").toLowerCase();
    Object.entries(CARRIER_ASSETS).forEach(([key, value]) => {
      if (carrierNameLower.includes(key.toLowerCase())) {
        url = value;
      }
    });

    return url;
  }, [data?.carrier_name]);

  if (isLoading || isRefetching) return <CircularProgressCentered />;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flex: 1,
        position: "relative",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          minWidth: 0,
          overflowY: "auto",
          marginRight: open ? "30%" : 0,
          transition: "margin-right 0.3s ease",
        }}
      >
        <Container>
          <Stack spacing={3}>
            <Stack direction="column" spacing={0}>
              <BackNavigationButton
                label="Bonus Forecast"
                route={`/${ROUTE_NAMES.BBE}/${brokerId}/${ROUTE_NAMES.BBE_FORECAST}`}
              />
              <Stack direction="row" spacing={0}>
                <Stack spacing={0} direction="column" flex={2}>
                  <Typography variant="header-medium" sx={{ fontWeight: 400 }}>
                    Carrier Bonus Detail
                  </Typography>
                  {/* TODO: Update once we are able to retrieve */}
                  {/* <Typography variant="body-medium" color="textSecondary">
                    Last salesforce sync {}
                  </Typography> */}
                </Stack>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => setOpen((prev) => !prev)}
                >
                  <Typography textTransform="uppercase" color="inherit">
                    Carrier Bonus Rules
                  </Typography>
                </Button>
              </Stack>
            </Stack>

            <Card variant="outlined">
              <CardContent sx={{ padding: "0 !important" }}>
                <Stack spacing={0} direction="column" p={3}>
                  <div style={{ flex: 2 }}>
                    {imageSrc && (
                      <Box component="img" alt="carrier-logo" src={imageSrc} />
                    )}
                  </div>
                  <Typography variant="header-medium" sx={{ fontWeight: 400 }}>
                    {data?.carrier_name} Bonus Summary
                  </Typography>
                </Stack>

                <Divider />

                <Stack padding={3} spacing={5}>
                  <Stack spacing={3}>
                    <Stack direction="row" spacing={3}>
                      <Typography variant="header-small">
                        Total Projected Bonus
                      </Typography>
                      <Typography variant="header-small">
                        {projectedBonus}
                      </Typography>
                    </Stack>

                    <Stack spacing={0}>
                      <Typography variant="body-medium">
                        <strong>Program Level</strong>
                      </Typography>
                      <Typography variant="body-medium">
                        {data?.bonus_description}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack spacing={3}>
                    <Typography variant="header-small">Persistency</Typography>

                    <Grid container spacing={2}>
                      {CARRIER_BONUS_DETAIL_PERSISTENCY_TITLE.map((t) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={t.title}>
                          <Stack spacing={0}>
                            <Typography variant="body-medium">
                              <strong>{t.title}</strong>
                            </Typography>
                            <Typography variant="body-medium">
                              {formatNumberValue(t.field, data?.[t.field])}
                            </Typography>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>

                  <Stack spacing={3}>
                    <Typography variant="header-small">New Business</Typography>

                    <Grid container spacing={2}>
                      {CARRIER_BONUS_DETAIL_BUSINESS_TITLE.map((t) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={t.title}>
                          <Stack spacing={0}>
                            <Typography variant="body-medium">
                              <strong>{t.title}</strong>
                            </Typography>
                            <Typography variant="body-medium">
                              {formatNumberValue(t.field, data?.[t.field])}
                            </Typography>
                          </Stack>
                        </Grid>
                      ))}
                    </Grid>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 0, paddingBottom: "0 !important" }}>
                <CardHeader title="In/Out Detail" sx={{ padding: 3 }} />
                <InOutTable carrierName={data?.carrier_name} />
              </CardContent>
            </Card>
          </Stack>
        </Container>
      </Box>

      <CarrierRulesDrawer
        open={open}
        setOpen={setOpen}
        brokerId={brokerId}
        carrierId={carrierId ?? ""}
        carrierName={data?.carrier_name as CarrierName}
      />
    </Box>
  );
};
